import _ from 'lodash'
import Backbone from 'backbone'
import { createWobs } from './wobs'
import GenericOverviewInfoDisplayHeader from 'scoreboard/components/overview-info/generic-overview-info-display-header'
import globals from 'scoreboard/components/globals'
import i18n from 'scoreboard/components/i18n'
import SendToOptions from 'scoreboard/components/send-to-options'
import Maestro from '../widgets/maestro'
import metadata from 'scoreboard/components/metadata'
import togglePrefixedClass from 'scoreboard/components/toggle-prefixed-class'
import exportState from 'scoreboard/components/export/export-state'
import DatasetFiltersLayout from './dataset-filters-layout'
import spectator from 'scoreboard/components/spectator'
import permissions from 'scoreboard/components/permissions'

// HBS
import tabs from './dashboards-tabs-template'

var DashboardsHeader = GenericOverviewInfoDisplayHeader.extend({
	redrawDisplay: function(newPeriodId) {
		this.trigger('change:period', newPeriodId)
	},

	styleAlertButton: function(hasAlerts) {
		GenericOverviewInfoDisplayHeader.prototype.styleAlertButton.apply(this, arguments)
		if (this.alertButton) {
			this.alertButton.find('.icon-menu').hide()
			this.alertButton
				.toggleClass('is-not-displayed', !permissions.bookmarks())
		}
	},

	spectate: function() {
		GenericOverviewInfoDisplayHeader.prototype.spectate.apply(this, arguments)
		if (spectator.getCurrent() && this._isNonFolder()) {
			// If we're viewing this dashboard as a part of a briefing, we have to
			// change the edit button to point to the edit page for that slide.
			this._updateSpectatedEditTab()
		}
	}
})

var DisplayView = Backbone.View.extend({
	deferredReady: true,
	tabsTemplate: tabs,

	className: 'display-layout display-layout-dashboards',

	/** @type {Dashboard} */
	model: null,

	initialize: function (options) {

		this.options = options || {}
		this.heading = this.model.isNew() ?
			i18n.get('common.create2', metadata[this.model.section].createTypeCode) :
			this.model.get('label')

		// #21875 after saving a new model, we need to update this.heading so
		// it won't still be something like "Create Dashboard"
		this.listenTo(this.model, 'change:label', () => this.heading = this.model.get('label'))

		let wobData = this.model.wobData()

		this._injestWobState(wobData)

		this.wobs = createWobs({
			wobData,
			dashboardModel: this.model,
			organizationId: this.model.get('organizationId'),
			getPeriodId: () => this.getPeriodId()
		})

		this.listenTo(this, 'resize', _.bind(function () {
			this.canvasContainer.resizeCanvas()
			this.datasetFiltersLayout.resize()
		}.bind(this)))

		this.maestro = new Maestro({
			data: this.model.data,
			embeddedToken: this.options.embeddedToken,
			state: options.state,
			getPeriodId: () => this.getPeriodId()
		})

		this.datasetFiltersLayout = new DatasetFiltersLayout({
			maestro: this.maestro,
			// #31682 Don't display Add button in Edit mode. We don't currently
			// persist dashboard-level filters, so it would be misleading to
			// allow the user to add them if they won't be saved
			allowAdd: this.subsection !== 'edit',
			// Hide the filters drawer initially; it will be shown after the
			// canvas is built if there are dataset widgets
			showDrawer: false
		})
	},

	getPeriodId () {
		// Dashboards in the "Dashboards" section use the calendar period selector
		return globals.periodId
	},

	_injestWobState: function (wobData) {
		// Export persist state and set in request scope, bookmarks/briefings pass in URL
		const state = exportState.state || this.options.state

		if (state && state.widgets) {
			wobData.forEach(w => {
				let wobState = state.widgets[w.id]
				if (wobState) {
					w.widget.state = wobState
				}
			})
		}
	},

	_sendToOptions: function() {
		let uiState = (opt) => this.getState(opt)
		return [].concat(
			SendToOptions.briefing({ uiState }),
			SendToOptions.exportServerOptions({ uiState }),
			SendToOptions.png({ uiState }),
			SendToOptions.sharedDashboard({
				section: this.model.section,
				uiState
			}))
	},

	// creates the tabs to show in the header
	_tabs: function() {
		return this.tabsTemplate(this.model.attributes)
	},

	render: function () {
		this.$el.empty().append(this.datasetFiltersLayout.el)
		this.datasetFiltersLayout.render()
		this.datasetFiltersLayout.resize()

		this.toggleBackground()
		this.model.on('change:background', () => this.toggleBackground())

		this.renderHeader()

		let container = this.datasetFiltersLayout.mainContentContainer()
		container.addClass('canvas-container')

		this.canvasContainer = this.createCanvasContainer(container)

		this.listenTo(this.canvasContainer, 'ready', () => {
			this.datasetFiltersLayout.renderFilters()
			this.updateFilterDrawerVisibility()

			this.listenTo(this.canvas.collection, 'add remove', () => {
				this.updateFilterDrawerVisibility()
			})
		})

		// important to add listeners before calling render(),
		// in case all the widgets render immediately (issue #9837)
		this._setupDeferredReadyEvent()

		this.canvasContainer.render()
		this.canvas = this.canvasContainer.canvas

		return this
	},

	updateFilterDrawerVisibility: function () {
		// During an update, the nested models might not be finished updating
		// before this is called, so just wait until nextTick for everything
		// to complete
		process.nextTick(() => {
			let hasDatasetWidgets = this.maestro.datasets().length > 0
			this.datasetFiltersLayout.toggleVisibility(hasDatasetWidgets)

			// If the drawer is visible, resize the canvas to compensate for it.
			if (!this.$('.display-layout').hasClass('is-hiding-drawer')) {
				this.canvas.resize()
			}
		})
	},

	renderHeader: function() {
		_.result(this.header, 'remove')
		this.header = new DashboardsHeader({
			heading: this.heading,
			model: this.model,
			// this.subsection must be set by subclasses
			subsection: this.subsection,
			sendToOptions: this.subsection === 'edit' ? null : this._sendToOptions(),
			tabs: this._tabs(),
			periods: true,
			guard: false,
			uiState: () => this.getState()
		})
		this.datasetFiltersLayout.setHeader(this.header)

		this.listenTo(this.header, 'change:period', function () {
			_(this.canvas.views)
				.values()
				.forEach(function(wobView) {
					wobView.view.trigger('change:period')
				})

			this.maestro.queryAll()
		}.bind(this))
	},

	toggleBackground() {
		togglePrefixedClass(this.el, 'dashboard-background', this.model.get('background'))
	},

	/**
	 * Returns the state of the UI (sorted columns, dataset drilldowns, etc) in
	 * a format that can be later used to reconstruct it.
	 *
	 * @return {Object} state
	 *
	 * @return {Array} state.liveFilters Any active dataset filters
	 *
	 * @return {Object} state.widgets The state of each widget. The object key is
	 * the widget ID, and the value varies depending on the widget implementation.
	 */
	getState: function (opt = {}) {
		let widgets = _(this.canvas.views)
			.values()
			.map(wobView => {
				return [wobView.model.id, wobView.view.getState(opt)]
			})
			.fromPairs()
			.value()

		return {
			widgets,
			liveFilters: this.maestro.liveFilters(),
			drilldownIdsByFieldId: this.maestro.drilldownIdsByFieldId()
		}
	},

	remove: function () {
		Backbone.View.prototype.remove.apply(this, this.arguments)
		_.invokeMap([this.header, this.maestro, this.canvasContainer],
			'remove')
	},

	/**
	 * Added for exports, notifies the main electron process
	 * when the page is ready to be captured.
	 * @private
	 */
	_setupDeferredReadyEvent: function () {
		this.listenToOnce(this.canvasContainer, 'exportReady', function () {
			// see export-preparer.js
			this.trigger('ready', {autoScale: true})
		})
	}
})

DisplayView.CANVAS_MIN_HEIGHT = 350

DisplayView.CANVAS_MIN_WIDTH = 550

export default DisplayView

DisplayView.__filename__ = 'dashboards/display/display.js'
